import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { 
  Mail, MessageSquare, Edit, Trash2, Plus, Save, X, RefreshCw, 
  Send, CheckCircle, AlertCircle, Eye, EyeOff, Copy, Info,
  Users, Bell, FileText
} from "lucide-react";

const CATEGORIES = [
  { value: "transaction", label: "Transfert de billets", color: "bg-blue-100 text-blue-800" },
  { value: "reminder", label: "Rappel", color: "bg-orange-100 text-orange-800" },
  { value: "cancellation", label: "Annulation & Remboursement", color: "bg-red-100 text-red-800" },
  { value: "dispute", label: "Litige", color: "bg-red-100 text-red-800" },
  { value: "admin", label: "Admin", color: "bg-purple-100 text-purple-800" },
];

const RECIPIENTS = [
  { value: "buyer", label: "Acheteur" },
  { value: "seller", label: "Vendeur" },
  { value: "admin", label: "Administrateur" },
];

// Variable chip component
const VariableChip = ({ variable, onClick }) => (
  <button
    type="button"
    onClick={() => onClick(variable)}
    className="inline-flex items-center px-2 py-0.5 text-xs bg-brand-100 text-brand-800 rounded-full hover:bg-brand-200 transition-colors cursor-pointer"
    title={`Cliquez pour copier {${variable}}`}
  >
    {`{${variable}}`}
  </button>
);

const NotificationTemplateManager = () => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showTestDialog, setShowTestDialog] = useState(false);
  const [testingTemplate, setTestingTemplate] = useState(null);
  const [testRecipient, setTestRecipient] = useState("");
  const [testType, setTestType] = useState("email");
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);

  useEffect(() => {
    fetchTemplates();
  }, []);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/admin/notification-templates`);
      setTemplates(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement des templates");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEditTemplate = (template) => {
    setEditingTemplate({ ...template });
    setShowEditDialog(true);
  };

  const handleSaveTemplate = async () => {
    if (!editingTemplate) return;
    
    setSaving(true);
    try {
      await axios.put(`${API}/admin/notification-templates/${editingTemplate.key}`, {
        name: editingTemplate.name,
        description: editingTemplate.description,
        enabled: editingTemplate.enabled,
        recipients: editingTemplate.recipients,
        sms_enabled: editingTemplate.sms_enabled,
        sms_message_fr: editingTemplate.sms_message_fr,
        sms_message_en: editingTemplate.sms_message_en,
        email_enabled: editingTemplate.email_enabled,
        email_subject_fr: editingTemplate.email_subject_fr,
        email_subject_en: editingTemplate.email_subject_en,
      });
      
      toast.success("Template mis à jour avec succès!");
      setShowEditDialog(false);
      setEditingTemplate(null);
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la sauvegarde");
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  const handleToggleEnabled = async (template, enabled) => {
    try {
      await axios.put(`${API}/admin/notification-templates/${template.key}`, { enabled });
      toast.success(enabled ? "Template activé" : "Template désactivé");
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la modification");
    }
  };

  const handleResetDefaults = async () => {
    if (!window.confirm("Êtes-vous sûr de vouloir réinitialiser tous les templates aux valeurs par défaut? Vos modifications seront perdues.")) {
      return;
    }
    
    setResetting(true);
    try {
      await axios.post(`${API}/admin/notification-templates/reset-defaults`);
      toast.success("Templates réinitialisés aux valeurs par défaut");
      fetchTemplates();
    } catch (error) {
      toast.error("Erreur lors de la réinitialisation");
    } finally {
      setResetting(false);
    }
  };

  const handleTestTemplate = (template) => {
    setTestingTemplate(template);
    setTestType(template.email_enabled ? "email" : "sms");
    setShowTestDialog(true);
  };

  const sendTestNotification = async () => {
    if (!testingTemplate || !testRecipient) {
      toast.error("Veuillez entrer un destinataire");
      return;
    }
    
    try {
      const response = await axios.post(
        `${API}/admin/notification-templates/${testingTemplate.key}/test`,
        null,
        { params: { test_type: testType, test_recipient: testRecipient } }
      );
      
      if (response.data.success) {
        toast.success(`Test ${testType === 'email' ? 'email' : 'SMS'} envoyé à ${testRecipient}`);
        setShowTestDialog(false);
        setTestRecipient("");
      }
    } catch (error) {
      toast.error(`Erreur: ${error.response?.data?.detail || "Échec de l'envoi"}`);
    }
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(`{${variable}}`);
    toast.success(`Variable {${variable}} copiée!`);
  };

  const insertVariable = (variable) => {
    if (editingTemplate) {
      // Insert into the focused field (simplified: append to SMS message)
      setEditingTemplate({
        ...editingTemplate,
        sms_message_fr: `${editingTemplate.sms_message_fr || ''}{${variable}}`
      });
    }
    copyVariable(variable);
  };

  const getCategoryBadge = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? (
      <Badge className={`${cat.color} font-normal`}>{cat.label}</Badge>
    ) : (
      <Badge variant="secondary">{category}</Badge>
    );
  };

  const filteredTemplates = selectedCategory === "all" 
    ? templates 
    : templates.filter(t => t.category === selectedCategory);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brand-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Modèles de notifications</h2>
          <p className="text-sm text-gray-600">
            Personnalisez les messages envoyés par email et SMS
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleResetDefaults}
            disabled={resetting}
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${resetting ? 'animate-spin' : ''}`} />
            Réinitialiser
          </Button>
        </div>
      </div>

      {/* Info Banner */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Variables disponibles</p>
            <p className="text-blue-700">
              Utilisez les variables entre accolades pour personnaliser vos messages. 
              Exemple: <code className="bg-blue-100 px-1 rounded">{"{event_name}"}</code> sera remplacé par le nom de l'événement.
            </p>
          </div>
        </div>
      </div>

      {/* Category Filter */}
      <div className="flex flex-wrap gap-2">
        <Button
          variant={selectedCategory === "all" ? "default" : "outline"}
          size="sm"
          onClick={() => setSelectedCategory("all")}
          className={selectedCategory === "all" ? "bg-brand-600 hover:bg-brand-700" : ""}
        >
          Tous ({templates.length})
        </Button>
        {CATEGORIES.map(cat => {
          const count = templates.filter(t => t.category === cat.value).length;
          return (
            <Button
              key={cat.value}
              variant={selectedCategory === cat.value ? "default" : "outline"}
              size="sm"
              onClick={() => setSelectedCategory(cat.value)}
              className={selectedCategory === cat.value ? "bg-brand-600 hover:bg-brand-700" : ""}
            >
              {cat.label} ({count})
            </Button>
          );
        })}
      </div>

      {/* Templates List */}
      <div className="grid gap-4">
        {filteredTemplates.map((template) => (
          <Card key={template.key} className={`${!template.enabled ? 'opacity-60' : ''}`}>
            <CardContent className="pt-4">
              <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                {/* Left: Info */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h3 className="font-semibold text-gray-900">{template.name}</h3>
                    {getCategoryBadge(template.category)}
                    {template.is_system && (
                      <Badge variant="outline" className="text-xs">Système</Badge>
                    )}
                    {!template.enabled && (
                      <Badge variant="secondary" className="bg-gray-100">Désactivé</Badge>
                    )}
                  </div>
                  
                  <p className="text-sm text-gray-600">{template.description}</p>
                  
                  <div className="flex items-center gap-4 text-sm">
                    <div className="flex items-center gap-1">
                      <Mail className={`h-4 w-4 ${template.email_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={template.email_enabled ? 'text-green-700' : 'text-gray-500'}>
                        Email {template.email_enabled ? 'actif' : 'inactif'}
                      </span>
                    </div>
                    <div className="flex items-center gap-1">
                      <MessageSquare className={`h-4 w-4 ${template.sms_enabled ? 'text-green-600' : 'text-gray-400'}`} />
                      <span className={template.sms_enabled ? 'text-green-700' : 'text-gray-500'}>
                        SMS {template.sms_enabled ? 'actif' : 'inactif'}
                      </span>
                    </div>
                  </div>

                  {/* Preview */}
                  {template.sms_message_fr && (
                    <div className="mt-2 p-2 bg-gray-50 rounded-md text-sm text-gray-700 font-mono text-xs">
                      {template.sms_message_fr.substring(0, 100)}
                      {template.sms_message_fr.length > 100 && '...'}
                    </div>
                  )}

                  {/* Variables */}
                  {template.available_variables?.length > 0 && (
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-xs text-gray-500">Variables:</span>
                      {template.available_variables.map(v => (
                        <VariableChip key={v} variable={v} onClick={copyVariable} />
                      ))}
                    </div>
                  )}
                </div>

                {/* Right: Actions */}
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleTestTemplate(template)}
                    disabled={!template.enabled}
                    title="Envoyer un test"
                  >
                    <Send className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleEditTemplate(template)}
                    title="Modifier"
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <button
                    onClick={() => handleToggleEnabled(template, !template.enabled)}
                    className={`p-2 rounded-md transition-colors ${
                      template.enabled 
                        ? 'bg-green-100 text-green-700 hover:bg-green-200' 
                        : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
                    }`}
                    title={template.enabled ? 'Désactiver' : 'Activer'}
                  >
                    {template.enabled ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {filteredTemplates.length === 0 && (
        <div className="text-center py-12 text-gray-500">
          Aucun template trouvé dans cette catégorie
        </div>
      )}

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit className="h-5 w-5 text-brand-600" />
              Modifier le template
            </DialogTitle>
            <DialogDescription>
              Personnalisez le contenu des notifications
            </DialogDescription>
          </DialogHeader>

          {editingTemplate && (
            <div className="space-y-6 py-4">
              {/* Basic Info */}
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Nom du template</Label>
                    <Input
                      value={editingTemplate.name || ''}
                      onChange={(e) => setEditingTemplate({...editingTemplate, name: e.target.value})}
                    />
                  </div>
                  <div>
                    <Label>Clé (non modifiable)</Label>
                    <Input value={editingTemplate.key} disabled className="bg-gray-50" />
                  </div>
                </div>
                
                <div>
                  <Label>Description</Label>
                  <Input
                    value={editingTemplate.description || ''}
                    onChange={(e) => setEditingTemplate({...editingTemplate, description: e.target.value})}
                  />
                </div>
              </div>

              {/* Variables Reference */}
              {editingTemplate.available_variables?.length > 0 && (
                <div className="bg-brand-50 p-3 rounded-lg">
                  <p className="text-sm font-medium text-brand-800 mb-2">Variables disponibles (cliquez pour copier):</p>
                  <div className="flex flex-wrap gap-2">
                    {editingTemplate.available_variables.map(v => (
                      <VariableChip key={v} variable={v} onClick={insertVariable} />
                    ))}
                  </div>
                </div>
              )}

              {/* SMS Section */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-5 w-5 text-brand-600" />
                    <h4 className="font-medium">Message SMS</h4>
                  </div>
                  <button
                    onClick={() => setEditingTemplate({...editingTemplate, sms_enabled: !editingTemplate.sms_enabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editingTemplate.sms_enabled ? 'bg-brand-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editingTemplate.sms_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {editingTemplate.sms_enabled && (
                  <>
                    <div>
                      <Label className="text-sm">Message français</Label>
                      <Textarea
                        value={editingTemplate.sms_message_fr || ''}
                        onChange={(e) => setEditingTemplate({...editingTemplate, sms_message_fr: e.target.value})}
                        className="font-mono text-sm"
                        rows={3}
                        placeholder="Entrez le message SMS en français..."
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(editingTemplate.sms_message_fr || '').length}/160 caractères
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm">Message anglais (optionnel)</Label>
                      <Textarea
                        value={editingTemplate.sms_message_en || ''}
                        onChange={(e) => setEditingTemplate({...editingTemplate, sms_message_en: e.target.value})}
                        className="font-mono text-sm"
                        rows={3}
                        placeholder="Enter SMS message in English..."
                      />
                    </div>
                  </>
                )}
              </div>

              {/* Email Section */}
              <div className="space-y-3 p-4 border rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Mail className="h-5 w-5 text-blue-600" />
                    <h4 className="font-medium">Email</h4>
                  </div>
                  <button
                    onClick={() => setEditingTemplate({...editingTemplate, email_enabled: !editingTemplate.email_enabled})}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      editingTemplate.email_enabled ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      editingTemplate.email_enabled ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {editingTemplate.email_enabled && (
                  <>
                    <div>
                      <Label className="text-sm">Objet français</Label>
                      <Input
                        value={editingTemplate.email_subject_fr || ''}
                        onChange={(e) => setEditingTemplate({...editingTemplate, email_subject_fr: e.target.value})}
                        placeholder="Sujet de l'email en français"
                      />
                    </div>
                    <div>
                      <Label className="text-sm">Objet anglais (optionnel)</Label>
                      <Input
                        value={editingTemplate.email_subject_en || ''}
                        onChange={(e) => setEditingTemplate({...editingTemplate, email_subject_en: e.target.value})}
                        placeholder="Email subject in English"
                      />
                    </div>
                    <p className="text-xs text-gray-500">
                      Note: Le contenu HTML des emails est généré automatiquement. Seul l'objet peut être modifié ici.
                    </p>
                  </>
                )}
              </div>
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              <X className="h-4 w-4 mr-2" />
              Annuler
            </Button>
            <Button 
              onClick={handleSaveTemplate} 
              disabled={saving}
              className="bg-brand-600 hover:bg-brand-700"
            >
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Test Dialog */}
      <Dialog open={showTestDialog} onOpenChange={setShowTestDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Send className="h-5 w-5 text-brand-600" />
              Tester le template
            </DialogTitle>
            <DialogDescription>
              Envoyez un test de "{testingTemplate?.name}"
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div>
              <Label>Type de notification</Label>
              <Select value={testType} onValueChange={setTestType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {testingTemplate?.email_enabled && (
                    <SelectItem value="email">
                      <div className="flex items-center gap-2">
                        <Mail className="h-4 w-4" />
                        Email
                      </div>
                    </SelectItem>
                  )}
                  {testingTemplate?.sms_enabled && (
                    <SelectItem value="sms">
                      <div className="flex items-center gap-2">
                        <MessageSquare className="h-4 w-4" />
                        SMS
                      </div>
                    </SelectItem>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>
                {testType === 'email' ? 'Adresse email' : 'Numéro de téléphone'}
              </Label>
              <Input
                value={testRecipient}
                onChange={(e) => setTestRecipient(e.target.value)}
                placeholder={testType === 'email' ? 'exemple@email.com' : '+15145551234'}
              />
            </div>

            <div className="bg-amber-50 p-3 rounded-lg text-sm text-amber-800">
              <p className="font-medium mb-1">⚠️ Note</p>
              <p>Le test utilisera des données fictives pour remplir les variables du template.</p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowTestDialog(false)}>
              Annuler
            </Button>
            <Button 
              onClick={sendTestNotification}
              className="bg-brand-600 hover:bg-brand-700"
              disabled={!testRecipient}
            >
              <Send className="h-4 w-4 mr-2" />
              Envoyer le test
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default NotificationTemplateManager;
